import _ from "lodash";
import ZMath from "./ZMath";
import randomColor from "randomcolor";
import Colors from "__SYSDefault/Colors";
import GState from "./__GState";

/**
 * @typedef {{
 *  r: Number,
 *  g: Number,
 *  b: Number,
 *  a: Number
 * }} color
 * 
 * @typedef {{
 *  r?: {
 *    min: Number,
 *    max: Number  
 *  },
 *  g?: {
 *    min: Number,
 *    max: Number  
 *  },
 *  b?: {
 *    min: Number,
 *    max: Number  
 *  },
 *  a?: {
 *    min: Number,
 *    max: Number  
 *  },
 * }} colorrange 
 * 
 * @typedef {import("../../__SYSDefault/Colors").customColorNames} customColorNames
 * @typedef {import("__SYSDefault/Colors").customGradientColorName} customGradientColorName
 * 
 * @typedef {"transparent" | "aliceblue" 
 * | "black" | "white" | "green" | "red" | "yellow" | "gold" 
 * | "blue" | "purple" | "grey" | "orange" | "lightBlue" | "skyBlue"
 * | "bgTransparent" | "bgDark" | "bgBlack" | "bgWhite"
 * | "bgGreen" | "bgRed" | "bgYellow" | "bgLightYellow"
 * | "bgBlue" | "bgPurple" | "bgGrey" | "bgOrange" 
 * | "bgLightBlue" | "bgGreyOut" | "bgSkyBlue" | "bgBlueSeparator"
 * | "Success" | "Warn" | "Info" | "Error" | "Warning"
 * | "bgSuccess" | "bgWarn" | "bgInfo" | "bgError"
 * | "lightRed" | "darkBox" | "darkRed" | "grey5" | "greyOut" | "lightGrey" | "lightGreen" | "placeholder"
 * | "cmBlue" | "dmWhite" | "cmBgBlue" | "cmHeaderBlue" | "dmBgDark" | "cmDarkBlue"
 * | "cmBtnRed" | "cmHoverRed" | "cmBtnGreen" | "cmHoverGreen" | "cmBtnBlue" | "cmHoverBlue"
 * | "cmBtnOrange" | "cmHoverOrange" | "cmBtnYellow" | "cmHoverYellow"
 * | "cmBtnPurple" | "cmHoverPurple" | "cmBtnGrey" | "cmHoverGrey"
 * | "cmBtnSkyBlue" | "cmHoverSkyBlue" 
 * | "dmBtnRed" | "dmHoverRed" | "dmBtnGreen" | "dmHoverGreen" | "dmBtnBlue" | "dmHoverBlue"
 * | "dmBtnOrange" | "dmHoverOrange" | "dmBtnYellow" | "dmHoverYellow"
 * | "dmBtnPurple" | "dmHoverPurple" | "dmBtnGrey" | "dmHoverGrey"
 * | "dmBtnSkyBlue" | "dmHoverSkyBlue"
 * | "pureRed" | "pureGreen" | "pureBlue" | "pureYellow" | "pureCyan"
 * | "pureMagenta" | "pureWhite" | "pureDark" | "pureOrange"
 * | "btnRed" | "btnGreen" | "btnBlue"
 * | "hoverRed" | "hoverGreen" | "hoverBlue" | "MUIError"
 * | "GPTo1" | "GPTGreen" | "GPT4" | "AIBg"
 * | customColorNames } colorName
 * 
 * @typedef { "fantasticBlue" | "rainbow"
 * | customGradientColorName } gradientColorName
 */

class ColorX {

  /**
   * @type {Object<string, color>}
   */
  static __staticColor = {
    transparent: this.RGBA(0, 0, 0, 0),
    aliceblue: this.RGBA(240, 248, 255, 1),

    //cell color fg
    black: this.RGBA(29, 29, 29, 1),
    white: this.RGBA(255, 255, 255, 1),
    green: this.RGBA(85, 136, 38, 1),
    red: this.RGBA(163, 1, 8, 1),
    yellow: this.RGBA(157, 88, 22, 1), 
    gold: this.RGBA(230, 200, 0, 1), 
    blue: this.RGBA(74, 98, 136, 1), 
    purple: this.RGBA(171, 84, 211, 1), 
    grey: this.RGBA(80, 80, 80, 1), 
    orange: this.RGBA(212, 120, 0, 1),
    lightBlue: this.RGBA(34, 130, 226, 1),
    skyBlue: this.RGBA(34, 130, 226, 1),
    
    //cell color bg
    bgTransparent: this.RGBA(0, 0, 0, 0),
    bgDark: this.RGBA(29, 25, 25, 1),
    bgBlack: this.RGBA(29, 29, 29, 1),
    bgWhite: this.RGBA(255, 255, 255, 1),
    bgGreen: this.RGBA(198, 239, 206, 1),
    bgRed: this.RGBA(255, 199, 206, 1),
    bgYellow: this.RGBA(255, 235, 156, 1),
    bgLightYellow: this.RGBA(255, 255, 224, 1),
    bgBlue: this.RGBA(155, 194, 230, 1),
    bgPurple: this.RGBA(250, 198, 255, 1),
    bgGrey: this.RGBA(197, 197, 197, 1),
    bgOrange: this.RGBA(255, 220, 191, 1),
    bgLightBlue: this.RGBA(175, 214, 250, 1),
    bgSkyBlue: this.RGBA(248, 251, 254, 1),
    bgGreyOut: this.RGBA(250, 250, 250, 1),
    bgBlueSeparator: this.RGBA(231, 243, 253, 1),

    //alert
    Success: this.RGBA(76, 175, 80, 1),
    Warn: this.RGBA(255, 152, 0, 1),
    Info: this.RGBA(33, 150, 243, 1),
    Error: this.RGBA(244, 67, 54, 1),
    bgSuccess: this.RGBA(198, 239, 206, 1),
    bgWarn: this.RGBA(255, 235, 156, 1),
    bgInfo: this.RGBA(155, 194, 230, 1),
    bgError: this.RGBA(255, 199, 206, 1),

    //special meaning
    Warning: this.RGBA(255, 220, 0, 1),

    //light | dark
    lightRed: this.RGBA(200, 0, 0, 1),
    darkBox: this.RGBA(60, 60, 60, 1),
    darkRed: this.RGBA(140, 1, 8, 1),
    grey5: this.RGBA(85, 85, 85, 1),
    greyOut: this.RGBA(160, 160, 160, 1),
    lightGrey: this.RGBA(220, 220, 220, 1),
    lightGreen: this.RGBA(160, 255, 160, 1),
    placeholder: this.RGBA(189, 189, 189, 1),

    //darkMode
    cmBlue: this.RGBA(125, 154, 200, 1),
    cmDarkBlue: this.RGBA(75, 104, 151, 1),
    dmWhite: this.RGBA(200, 200, 200, 1),
    cmBgBlue: this.RGBA(248, 250, 255, 1),
    cmHeaderBlue: this.RGBA(238, 250, 255, 1),
    dmBgDark: this.RGBA(40, 40, 40, 1),

    //darkMode btn
    cmBtnRed: this.RGBA(160, 0, 0, 1),
    cmHoverRed: this.RGBA(200, 0, 0, 1),
    cmBtnGreen: this.RGBA(85, 130, 38, 1),
    cmHoverGreen: this.RGBA(85, 160, 38, 1),
    cmBtnBlue: this.RGBA(50, 50, 130, 1),
    cmHoverBlue: this.RGBA(50, 50, 180, 1),
    cmBtnOrange: this.RGBA(255, 128, 0, 1),
    cmHoverOrange: this.RGBA(255, 160, 0, 1),
    cmBtnYellow: this.RGBA(255, 200, 0, 1),
    cmHoverYellow: this.RGBA(255, 220, 0, 1),
    cmBtnPurple: this.RGBA(150, 0, 150, 1),
    cmHoverPurple: this.RGBA(180, 0, 180, 1),
    cmBtnGrey: this.RGBA(100, 100, 100, 1),
    cmHoverGrey: this.RGBA(120, 120, 120, 1),
    cmBtnSkyBlue: this.RGBA(0, 150, 255, 1),
    cmHoverSkyBlue: this.RGBA(0, 180, 255, 1),
    dmBtnRed: this.RGBA(200, 0, 0, 1),
    dmHoverRed: this.RGBA(200, 0, 0, 1),
    dmBtnGreen: this.RGBA(85, 180, 38, 1),
    dmHoverGreen: this.RGBA(85, 160, 38, 1),
    dmBtnBlue: this.RGBA(125, 154, 200, 1),
    dmHoverBlue: this.RGBA(50, 50, 180, 1),
    dmBtnOrange: this.RGBA(255, 128, 0, 1),
    dmHoverOrange: this.RGBA(255, 160, 0, 1),
    dmBtnYellow: this.RGBA(255, 200, 0, 1),
    dmHoverYellow: this.RGBA(255, 220, 0, 1),
    dmBtnPurple: this.RGBA(150, 0, 150, 1),
    dmHoverPurple: this.RGBA(180, 0, 180, 1),
    dmBtnGrey: this.RGBA(100, 100, 100, 1),
    dmHoverGrey: this.RGBA(120, 120, 120, 1),
    dmBtnSkyBlue: this.RGBA(0, 150, 255, 1),
    dmHoverSkyBlue: this.RGBA(0, 180, 255, 1), 

    //btn
    btnRed: this.RGBA(160, 0, 0, 1),
    hoverRed: this.RGBA(200, 0, 0, 1),
    btnGreen: this.RGBA(85, 130, 38, 1),
    hoverGreen: this.RGBA(85, 160, 38, 1),
    btnBlue: this.RGBA(50, 50, 130, 1),
    hoverBlue: this.RGBA(50, 50, 180, 1),

    //pure
    pureRed: this.RGBA(255, 0, 0, 1),
    pureGreen: this.RGBA(0, 255, 0, 1),
    pureBlue: this.RGBA(0, 0, 255, 1),
    pureYellow: this.RGBA(255, 255, 0, 1), 
    pureCyan: this.RGBA(0, 255, 255, 1),
    pureMagenta: this.RGBA(255, 0, 255, 1),
    pureWhite: this.RGBA(255, 255, 255, 1),
    pureDark: this.RGBA(0, 0, 0, 1),
    pureOrange: this.RGBA(255, 128, 0, 1),

    //MUI
    MUIError: this.RGBA(211, 47, 47, 1),

    //External
    GPTo1: this.RGBA(199, 186, 0, 1),
    GPTGreen: this.RGBA(27, 179, 137, 1),
    GPT4: this.RGBA(0, 113, 189, 1),
    AIBg: this.RGBA(202, 226, 250, 1),
    ...Colors.__custom
  };

  /**
   * @type {Object.<string, [{
   *  percent: Number,
   *  color: color
   * }]}
   */
  static __gradient = {
    fantasticBlue: [
      {
        percent: 0,
        color: {r: 67, g: 169, b: 223, a: 1}
      },
      {
        percent: 75.75,
        color: {r: 31, g: 103, b: 176, a: 1}
      },
      {
        percent: 100,
        color: {r: 31, g: 103, b: 176, a: 1}
      },
    ],
    rainbow: [
      {
        percent: 0,
        color: {r: 255, g: 0, b: 0, a: 1}
      },
      {
        percent: 16.6,
        color: {r: 255, g: 127, b: 0, a: 1}
      },
      {
        percent: 33.3,
        color: {r: 255, g: 255, b: 0, a: 1}
      },
      {
        percent: 50,
        color: {r: 0, g: 255, b: 0, a: 1}
      },
      {
        percent: 66.6,
        color: {r: 0, g: 0, b: 255, a: 1}
      },
      {
        percent: 83.3,
        color: {r: 75, g: 0, b: 130, a: 1}
      },
      {
        percent: 100,
        color: {r: 148, g: 0, b: 211, a: 1}
      }
    ],
    ...Colors.__gradient
  };

  /**
   * 
   * @param {Number} r 
   * @param {Number} g 
   * @param {Number} b 
   * @param {Number} a 
   * @returns {color}
   */
  static RGBA(r, g, b, a = 1){
    return {r, g, b, a};
  }

  /**
   * Get the color from the mapping (Cut FuncName)
   * @param {colorName | color} name 
   * @param {Number} a 
   * @returns
   */
  static CSS(name, a = undefined){
    return ColorX.GetColorCSS(name, a);
  }

  /**
   * 
   * @param {{
   *  day: colorName | color,
   *  night: colorName | color
   * }} param0 
   * @param {Number} a 
   * @param {Boolean} darkMode 
   * @returns 
   */
  static DayNight({day, night}, a = undefined, darkMode = GState.darkMode){
    return this.CSS(darkMode ? night : day, a);
  }

  /**
   * Get the bgcolor from the mapping (Cut FuncName)
   * @param {colorName | color} name 
   * @param {Number} a 
   * @returns
   */
  static BgCSS(name, a = undefined){
    return ColorX.GetBGColorCSS(name, a);
  }

  /**
   * 
   * @param {colorName | color} name 
   * @param {Number} a 
   * @returns {color}
   */
  static GetColor(name, a = undefined){
    if(!name) return undefined;

    if(_.isString(name)){
      if(!ColorX.__staticColor[name]) {
        return name;
      }
      if(a === null || a === undefined){
        return ColorX.__staticColor[name];
      }
      return {...ColorX.__staticColor[name], a};
    }

    if(_.isObject(name)){
      if(a){
        return {...name, a}; 
      }
      return {...name}; 
    }
  }

  /**
   * Get the color from the mapping
   * @param {colorName | color} name 
   * @param {Number} a 
   * @returns
   */
  static GetColorCSS(name, a = undefined){
    let color = ColorX.GetColor(name, a);
    return ColorX.toCSS(color);
  }

  /**
   * Get the color with bgXxxx 
   * @param {colorName | color} name 
   * @param {Number} a 
   * @returns 
   */
  static GetBGColorCSS(name, a = undefined){
    if(!name) return undefined;

    if(_.isString(name)){
      let _name = "bg" + name.charAt(0).toUpperCase() + name.slice(1);
      return ColorX.CSS(_name, a);
    }

    if(_.isObject(name)){
      return ColorX.CSS(name, a);
    }
  }

  /**
   * 
   * @param {colorrange} range
   * @returns 
   */
  static GetRandom(range){
    let color = {
        r: ZMath.Cap(ZMath.SeedRandomInt("r", range?.r?.min ?? 0, range?.r?.max ?? 255), 0, 255),
        g: ZMath.Cap(ZMath.SeedRandomInt("g", range?.g?.min ?? 0, range?.g?.max ?? 255), 0, 255),
        b: ZMath.Cap(ZMath.SeedRandomInt("b", range?.b?.min ?? 0, range?.b?.max ?? 255), 0, 255),
      };
    let randomA = ZMath.Cap(ZMath.SeedRandomInt("a", range?.a?.min ?? 1, range?.a?.max ?? 1), 0, 1);
    return {
      ...color,
      a: randomA
    };
  }

   /**
   * 
   * @param {String} seed 
   * @returns 
   */
  static GetAvatarRandom(seed){
    return randomColor({seed});
  }

  /**
   * 
   * @param {String} seed 
   * @param {colorrange} range 
   * @returns 
   */
  static GetSeedRandom(seed, range){
    
    let color;
    let randomA = 1;
    if(_.isEmpty(seed)){
      return this.GetRandom(range);
    }else{
      color = {
        r: ZMath.Cap(ZMath.SeedRandomInt(seed + "r", range?.r?.min ?? 0, range?.r?.max ?? 255), 0, 255),
        g: ZMath.Cap(ZMath.SeedRandomInt(seed + "g", range?.g?.min ?? 0, range?.g?.max ?? 255), 0, 255),
        b: ZMath.Cap(ZMath.SeedRandomInt(seed + "b", range?.b?.min ?? 0, range?.b?.max ?? 255), 0, 255),
      };
      randomA = ZMath.Cap(ZMath.SeedRandomInt(seed + "a", range?.a?.min ?? 1, range?.a?.max ?? 1), 0, 1);
    }
    return {
      ...color,
      a: randomA
    };
  }

  static GetOpposite(color){
    let oppositeColor = {
      r: 255 - color.r,
      g: 255 - color.g,
      b: 255 - color.b,
      a: color.a
    };
    return oppositeColor;
  }

  /**
   * 
   * @param {colorrange} range 
   */
  static GetRandomCSS(range){
    let color = this.GetRandom(range);
    return this.GetColorCSS(color);
  }

  /**
   * Preset 0.2 alpha
   * @param {colorName | color} name 
   * @returns 
   */
  static GetBorderColorCSS(name){
    if(!name) return undefined;
    return ColorX.CSS(name, 0.2);
  }

  /**
   * Preset "0 0 2px 2px", alpha 0.2
   * @param {colorName | color} name 
   * @returns 
   */
  static GetBoxShadowCSS(name){
    if(!name) return undefined;
    return "0 0 2px 2px " + ColorX.CSS(name, 0.2);
  }

  /**
   * Get the linear gradient mapping
   * @param {gradientColorName} name 
   * @param {Number} deg 
   * @param {Number} a 
   * @returns 
   */
  static GetLinearGradient(name, deg, a = undefined){
    if(!name) return undefined;
    if(!ColorX.__gradient[name]) {
      console.warn("Gradient " + name + " not found.");
      return undefined;
    }
    return ColorX.linearGradientToCSS(deg, ColorX.__gradient[name], a);
  }

  /**
   * 
   * @param {colorName | color} name 
   * @param {Number} brightness 
   * @param {Number} glow  
   * @returns 
   */
  static GetLEDLight(name, brightness = 1, glow = 50){
    let color = ColorX.GetColor(name);
    let gradient = [
      {
        percent: 0,
        color: {...color, a: brightness}
      },
      {
        percent: glow,
        color: {...color, a: 0}
      },
      {
        percent: 100,
        color: {...color, a: 0}
      }
    ];
    return ColorX.radialGradientToCSS("circle at center", gradient); 
  }

  /**
   * Get the radial gradient mapping
   * @param {gradientColorName} name
   * @param {String} mode 
   * @param {Number} a 
   * @returns 
   */
  static GetRadialGradientCSS(name, mode = "circle at center", a = undefined){
    if(!name) return undefined;
    if(!ColorX.__gradient[name]) {
      console.warn("Gradient " + name + " not found.");
      return undefined;
    }
    return ColorX.radialGradientToCSS(mode, ColorX.__gradient[name], a);
  }

  /**
   * Get Linear gradient CSS token 
   * @param {Number} deg 
   * @param {color[]} colors 
   * @param {Number} a 
   * @returns 
   */
  static linearGradientToCSS(deg = 0, colors = [], a = undefined){
    let str = "linear-gradient(" + deg + "deg, ";
    str += ColorX.grandientColorsToCSS(colors, a);
    str += ")";
    return str;
  }

  /**
   * Get Radial Gradient CSS token
   * @param {String} mode 
   * @param {color[]} colors 
   * @param {Number} a 
   * @returns 
   */
  static radialGradientToCSS(mode = "circle at center", colors = [], a = undefined){
    let str = "radial-gradient(" + mode + ", ";
    str += ColorX.grandientColorsToCSS(colors, a);
    str += ")";
    return str;
  } 

  /**
   * Get Gradient CSS token
   * @param {color[]} colors 
   * @param {Number} a 
   * @returns 
   */
  static grandientColorsToCSS(colors, a){
    let str = "";
    _.map(colors, (o, i) => {
      if(i !== 0) str += ", ";
      str += ColorX.partToCSS(o, a);
    });
    return str;
  }

  /**
   * 
   * @param {{
   *   percent: Number,
   *   color: color
   * }} param0 
   * @param {Number} a 
   * @returns 
   */
  static partToCSS({percent, color}, a = undefined){
    if(a) return ColorX.toCSS({...color}, a) + " " + percent + "%";
    return ColorX.toCSS({...color}) + " " + percent + "%";
  }

  /**
   * Generate Rainbow CSS token
   * @param {Number} start 
   * @param {Number} section 
   * @param {Number} a 
   * @returns 
   */
  static Rainbow(start = 0, section = 7, a = undefined){
    return ColorX.IntervalCSS(start, start + 360, section, a);
  }

  /**
   * Get Array of CSS gradiant from a range of hue degree
   * @param {Number} startHue 
   * @param {Number} endHue 
   * @param {Number} section 
   * @param {Number} a 
   * @returns 
   */
  static Intervals(startHue, endHue, section = 7, a = undefined){
    let _range = _.range(section);
    let endH = endHue;
    if(endHue < startHue) endH = endHue + 360;

    let rtn = [];
    _.map(_range, (o, i) => {
      rtn.push(ColorX.HSL2RGB({
        h: startHue + ((endH - startHue) / section * i) % 360, a: a
      }));
    });
    return rtn;
  }

  /**
   * Get Interval CSS array
   * @param {Number} startHue 
   * @param {Number} endHue 
   * @param {Number} section 
   * @param {Number} a 
   * @returns 
   */
  static IntervalCSS(startHue, endHue, section = 7, a = undefined){
    let colors = ColorX.Intervals(startHue, endHue, section, a);
    let rtn = [];
    _.map(colors, (o, i) => {
      rtn.push(ColorX.toCSS(o));
    });
    return rtn;
  }

  /**
   * Get linear interval CSS array
   * @param {Number} deg 
   * @param {Number} startHue 
   * @param {Number} endHue 
   * @param {Number} section 
   * @param {Number} a 
   * @returns 
   */
  static LinearInterval(deg, startHue, endHue, section = 7, a = undefined){
    let __colors = this.GenColors(startHue, endHue, section = 7, a = undefined);
    return ColorX.linearGradientToCSS(deg, __colors, a);
  }

  /**
   * Get linear rainbow 
   * @param {Number} deg 
   * @param {Number} section 
   * @param {Number} a 
   * @returns 
   */
  static LinearRainbow(deg = 0, section = 7, a = undefined){
    return ColorX.LinearInterval(deg, 0, 360, section, a);
  }

  static GenColors(startHue, endHue, section = 7, a = undefined){
    let _range = _.range(section + 1);
    let endH = endHue;
    if(endHue < startHue) endH = endHue + 360;

    let __colors = [];
    _.map(_range, (o, i) => {
      __colors.push({
        percent: Math.fround(100 / section * i),
        color: ColorX.HSL2RGB({
          h: startHue + ((endH - startHue)  / section * i) % 360, 
          a: a})
      });
    });
    return __colors;
  }

  /**
   * HSL to RGB
   * @param {Object} param0 
   * @param {Number} param0.h 
   * @param {Number} [param0.s=1] 
   * @param {Number} [param0.l=0.5] 
   * @param {Number} [param0.a=1] 
   * @returns {color}
   */
  static HSL2RGB({h, s = 1, l = 0.5, a = 1}) {

    let c = (1 - Math.abs(2 * l - 1)) * s,
    x = c * (1 - Math.abs((h / 60) % 2 - 1)),
    m = l - c/2,
    r = 0,
    g = 0,
    b = 0;

    if (0 <= h && h < 60) {
      r = c; g = x; b = 0;  
    } else if (60 <= h && h < 120) {
      r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
      r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
      r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
      r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
      r = c; g = 0; b = x;
    }
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);
    
    return {r: r, g: g, b: b, a: a};
  }

  /**
   * 
   * @param {color} color 
   */
  static RGB2HSL(color){
    let _r = color.r / 255;
    let _g = color.g / 255;
    let _b = color.b / 255;
    let Cmax = Math.max(_r, _g, _b);
    let Cmin = Math.min(_r, _g, _b);
    let delta = Cmax- Cmin;

    let max = "r";
    if(_r >= _g && _r >= _b) max = "r";
    else if(_g >= _r && _g >= _b) max = "g";
    else max = "b";

    let h = 0;
    if(delta !== 0){
      if(max === "_r"){
        h = 60 * (((_g - _b) / delta) % 6);
      }else if (max === "_g"){
        h = 60 * (((_b - _r) / delta) + 2);
      }else{
        h = 60 * (((_r - _g) / delta) + 4);
      }
    }

    let l = (Cmax + Cmin) / 2;

    let s = 0;
    if(delta !== 0){
      s = delta / (1 - Math.abs(2*l - 1));
    }
    
    return {h: h, s: s, l: l, a: color.a};
  }

  /**
   * RGBA object to CSS string
   * @param {color} color 
   * @returns 
   */
  static toCSS(color){
    if(!color) return undefined;
    if(_.isString(color)) return color;
    let {r, g, b, a} = color;
    return "rgba("+ r + "," + g + "," + b + "," + (a ?? 1) + ")";
  }

  /**
   * RGB to HEX
   * @param {Number} r 
   * @param {Number} g 
   * @param {Number} b 
   * @returns 
   */
  static toHex(r, g, b){
    let _r = r.toString(16);
    let _g = g.toString(16);
    let _b = b.toString(16);

    if (_r.length === 1)
      _r = "0" + _r;
    if (g.length === 1)
      _g = "0" + _g;
    if (b.length === 1)
      _b = "0" + _b;

    return "#" + _r + _g + _b;
  }

  /**
   * @param {colorName | color} name 
   * @param {Number} percentage 
   * @param {Number} [a=1] 
   * @returns {color}
   */
  static Saturate(name, percentage, a = 1){
    let color = ColorX.GetColor(name, a);
    let hsl = this.RGB2HSL(color);
    hsl.s = ZMath.Cap(hsl.s * (1 + percentage / 100), 0, 1);
    return this.HSL2RGB(hsl);
  }

   /**
   * @param {colorName | color} name 
   * @param {Number} percentage 
   * @param {Number} [a=1] 
   * @returns {color}
   */
  static Desaturate(name, percentage, a = 1){
    let color = ColorX.GetColor(name, a);
    let hsl = this.RGB2HSL(color);
    hsl.s = ZMath.Cap(hsl.s * (1 - percentage / 100), 0, 1);
    return this.HSL2RGB(hsl);
  }

   /**
   * @param {colorName | color} name 
   * @param {Number} percentage 
   * @param {Number} [a=1] 
   * @returns {color}
   */
  static Lighten(name, percentage, a = 1){
    let color = ColorX.GetColor(name, a);
    let hsl = this.RGB2HSL(color);
    hsl.l = ZMath.Cap(hsl.l * (1 + percentage / 100), 0, 1);
    return this.HSL2RGB(hsl);
  }

   /**
   * @param {colorName | color} name 
   * @param {Number} percentage 
   * @param {Number} [a=1] 
   * @returns {color}
   */
  static Darken(name, percentage, a = 1){
    let color = ColorX.GetColor(name, a);
    let hsl = this.RGB2HSL(color);
    hsl.l = ZMath.Cap(hsl.l * (1 - percentage / 100), 0, 1);
    return this.HSL2RGB(hsl);
  }

  /**
   * 
   * @param {String} css 
   * @returns {color}
   */
  static ExtractCSS(css){
    let regex = /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/g;
    let result = regex.exec(css);
    return this.RGBA(Number(result[1]), Number(result[2]), 
      Number(result[3]), Number(result[4]));
  }

  /**
   * 
   * @param {color} color 
   * @param {*} matrix 
   * @returns 
   */
  static MultiplyRGB(color, matrix) {
    const newR = ZMath.Cap(color.r * matrix[0] + color.g * matrix[1] + color.b * matrix[2], 0, 255);
    const newG = ZMath.Cap(color.r * matrix[3] + color.g * matrix[4] + color.b * matrix[5], 0, 255);
    const newB = ZMath.Cap(color.r * matrix[6] + color.g * matrix[7] + color.b * matrix[8], 0, 255);
    color.r = newR;
    color.g = newG;
    color.b = newB;
    return color;
  }

  /**
   * 
   * @param {color} color 
   * @param {Number} value 
   * @returns 
   */
  static InvertRGB(color, value = 1) {
    color.r = ZMath.Cap((value + color.r / 255 * (1 - 2 * value)) * 255, 0, 255);
    color.g = ZMath.Cap((value + color.g / 255 * (1 - 2 * value)) * 255, 0, 255);
    color.b = ZMath.Cap((value + color.b / 255 * (1 - 2 * value)) * 255, 0, 255);
    return color;
  }


  static LinearRGB(color, slope = 1, intercept = 0) {
    color.r = ZMath.Cap(color.r * slope + intercept * 255, 0, 255);
    color.g = ZMath.Cap(color.g * slope + intercept * 255, 0, 255);
    color.b = ZMath.Cap(color.b * slope + intercept * 255, 0, 255);
    return color;
  }

  static ContrastRGB(color, value = 1) {
    return this.LinearRGB(color, value, -(0.5 * value) + 0.5);
  }

  static BrightnessRGB(color, value = 1) {
    return this.LinearRGB(color, value);
  }

  static HueRotateRGB(color, angle = 0) {
    angle = angle / 180 * Math.PI;
    const sin = Math.sin(angle);
    const cos = Math.cos(angle);

    return this.MultiplyRGB(color, [
      0.213 + cos * 0.787 - sin * 0.213,
      0.715 - cos * 0.715 - sin * 0.715,
      0.072 - cos * 0.072 + sin * 0.928,
      0.213 - cos * 0.213 + sin * 0.143,
      0.715 + cos * 0.285 + sin * 0.140,
      0.072 - cos * 0.072 - sin * 0.283,
      0.213 - cos * 0.213 - sin * 0.787,
      0.715 - cos * 0.715 + sin * 0.715,
      0.072 + cos * 0.928 + sin * 0.072,
    ]);
  }

  static GrayscaleRGB(color, value = 1) {
    return this.MultiplyRGB(color, [
      0.2126 + 0.7874 * (1 - value),
      0.7152 - 0.7152 * (1 - value),
      0.0722 - 0.0722 * (1 - value),
      0.2126 - 0.2126 * (1 - value),
      0.7152 + 0.2848 * (1 - value),
      0.0722 - 0.0722 * (1 - value),
      0.2126 - 0.2126 * (1 - value),
      0.7152 - 0.7152 * (1 - value),
      0.0722 + 0.9278 * (1 - value),
    ]);
  }

  static SepiaRGB(color, value = 1) {
    return this.MultiplyRGB(color, [
      0.393 + 0.607 * (1 - value),
      0.769 - 0.769 * (1 - value),
      0.189 - 0.189 * (1 - value),
      0.349 - 0.349 * (1 - value),
      0.686 + 0.314 * (1 - value),
      0.168 - 0.168 * (1 - value),
      0.272 - 0.272 * (1 - value),
      0.534 - 0.534 * (1 - value),
      0.131 + 0.869 * (1 - value),
    ]);
  }

  static SaturateRGB(color, value = 1) {
    return this.MultiplyRGB(color, [
      0.213 + 0.787 * value,
      0.715 - 0.715 * value,
      0.072 - 0.072 * value,
      0.213 - 0.213 * value,
      0.715 + 0.285 * value,
      0.072 - 0.072 * value,
      0.213 - 0.213 * value,
      0.715 - 0.715 * value,
      0.072 + 0.928 * value,
    ]);
  }

}

export default ColorX;